import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ShepherdStatus } from "./types";

function formatSlope(slope: number): string {
  const abs = Math.abs(slope * 100);
  const dir = slope > 0.02 ? "↑" : slope < -0.02 ? "↓" : "→";
  return `${dir}${abs.toFixed(0)}`;
}

/**
 * The always-visible ambient status pill: Shepherd state and coherence. Kept
 * to a single line for every tier — it's the brand identity shown in the
 * landing-page preview too, not diagnostic output.
 *
 * Everything below tier-line detail (cognition posture, subscores,
 * trajectory, predictive alerts) is a Memnora Pro feature — see
 * `AdvancedResonanceBadges` — and is not rendered here regardless of tier.
 */
export function CovenantCoherenceBadge() {
  const { data } = useQuery<ShepherdStatus>({
    queryKey: ["/api/shepherd/status"],
    refetchInterval: 8000,
  });

  if (!data || !data.lastEvaluation) return null;

  const cohPct = Math.round(data.coherence * 100);
  const stateLabel = data.triState === 1 ? "+1" : data.triState === -1 ? "-1" : "0";

  const hasPredictive = data.predictiveWarnings && data.predictiveWarnings.length > 0;
  const hasImminent = hasPredictive && data.predictiveWarnings.some(w => w.horizon <= 2);

  const cohColor = hasImminent
    ? "border-violet-500/50 bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-400/90"
    : cohPct >= 70
      ? "border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400/90"
      : cohPct >= 45
        ? "border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400/90"
        : "border-red-500/40 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400/90";

  const stateColor = data.triState === 1
    ? "bg-emerald-400"
    : data.triState === -1
      ? "bg-red-400"
      : "bg-amber-400";

  return (
    <div className="flex flex-wrap items-center gap-2 mb-1.5 text-[10px] font-mono" data-testid="covenant-coherence-badge">
      <div className={cn("flex items-center gap-1.5 px-2 py-0.5 rounded-full border", cohColor)}>
        <span className={cn("h-1.5 w-1.5 rounded-full", stateColor, hasImminent && "animate-pulse")} />
        <span>Shepherd: {stateLabel === "+1" ? "Ascending" : stateLabel === "-1" ? "Descending" : "Centered"}</span>
        <span className="opacity-60">|</span>
        <span>Coherence {cohPct}%</span>
      </div>
    </div>
  );
}

/**
 * Everything the raw instrumentation used to dump on every user, gated to
 * accounts with `advancedResonance` (Memnora Pro) — matches what the pricing
 * table already promises ("Advanced Resonance tools", "Advanced analytics /
 * visualizations"). Free/Plus/guest sessions never fetch this data at all.
 */
export function AdvancedResonanceBadges() {
  const { data } = useQuery<ShepherdStatus>({
    queryKey: ["/api/shepherd/status"],
    refetchInterval: 8000,
  });

  if (!data || !data.lastEvaluation) return null;

  const cohPct = Math.round(data.coherence * 100);
  const hasPredictive = data.predictiveWarnings && data.predictiveWarnings.length > 0;
  const hasImminent = hasPredictive && data.predictiveWarnings.some(w => w.horizon <= 2);

  return (
    <div className="flex flex-wrap items-center gap-2 mb-1.5 text-[10px] font-mono" data-testid="advanced-resonance-badges">
      {data.semanticDetections > 0 && (
        <div className="rounded-full border border-cyan-500/30 px-2 py-0.5 text-cyan-400/80">
          SEM {data.semanticDetections}
        </div>
      )}
      {data.stabilizationActive && (
        <div className="text-amber-400/70 px-1" data-testid="shepherd-stabilizing">
          Stabilizing
        </div>
      )}
      {data.conflictCount > 0 && (
        <div className="text-red-400/60 px-1" data-testid="shepherd-conflicts">
          {data.conflictCount} conflict{data.conflictCount > 1 ? "s" : ""}
        </div>
      )}
      {data.overrideCount > 0 && (
        <div className="text-amber-400/60 px-1" data-testid="shepherd-overrides">
          {data.overrideCount} override{data.overrideCount > 1 ? "s" : ""}
        </div>
      )}
      {hasPredictive && (
        <div className={cn("px-1.5 py-0.5 rounded-full border", hasImminent ? "border-violet-500/50 text-violet-700 dark:text-violet-400/90 bg-violet-50 dark:bg-violet-950/20" : "border-amber-500/30 text-amber-700 dark:text-amber-400/70")} data-testid="shepherd-predictive">
          {hasImminent ? "PREDICTIVE ALERT" : "TRAJECTORY WATCH"}: {data.predictiveWarnings.map(w => w.type.replace(/_/g, " ")).join(", ")}
        </div>
      )}
      {data.cognition && (
        <div className={cn(
          "flex items-center gap-1.5 px-2 py-0.5 rounded-full border",
          data.cognition.integrity < 0.4
            ? "border-red-500/50 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400/80"
            : data.cognition.posture === "DEFENSIVE" || data.cognition.posture === "RESTRICTIVE"
              ? "border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400/80"
              : "border-blue-500/30 bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400/70"
        )} data-testid="cognition-badge">
          <span className={cn("h-1.5 w-1.5 rounded-full",
            data.cognition.integrity < 0.4 ? "bg-red-400 animate-pulse" :
            data.cognition.posture === "EXPANSIVE" ? "bg-blue-400" : "bg-amber-400"
          )} />
          <span>{data.cognition.posture}</span>
          <span className="opacity-60">|</span>
          <span>Integrity {Math.round(data.cognition.integrity * 100)}%</span>
        </div>
      )}
      {data.subscores && (
        <div className="text-muted-foreground/40 ml-auto hidden sm:flex gap-2" data-testid="shepherd-subscores">
          <span>T:{Math.round(data.subscores.trajectory * 100)}</span>
          <span>E:{Math.round(data.subscores.emotionalConsistency * 100)}</span>
          <span>I:{Math.round(data.subscores.identityAlignment * 100)}</span>
          <span>R:{Math.round(data.subscores.resonance * 100)}</span>
          {data.trajectoryWindow && (
            <>
              <span className="opacity-40">|</span>
              <span className={data.trajectoryWindow.urgencySlope > 0.05 ? "text-red-400/60" : ""}>U{formatSlope(data.trajectoryWindow.urgencySlope)}</span>
              <span className={data.trajectoryWindow.trustSlope < -0.05 ? "text-red-400/60" : ""}>Tr{formatSlope(data.trajectoryWindow.trustSlope)}</span>
              <span className={data.trajectoryWindow.coherenceSlope < -0.05 ? "text-amber-400/60" : ""}>C{formatSlope(data.trajectoryWindow.coherenceSlope)}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Deep Memory engine status — folded into the advanced/Pro instrumentation. */
export function EngineModeBadge() {
  const { data } = useQuery<{ embeddingsAvailable: boolean; embeddingCalls: number; fallbackCalls: number; mode: string }>({
    queryKey: ["/api/resonance/engine-status"],
    refetchInterval: 15000,
  });

  if (!data) return null;

  return (
    <div className="flex items-center gap-2 mb-1.5 text-[10px] font-mono" data-testid="engine-mode-badge">
      <div className={cn(
        "flex items-center gap-1.5 px-2 py-0.5 rounded-full border",
        data.embeddingsAvailable
          ? "border-green-500/30 bg-green-950/30 text-green-400/80"
          : "border-amber-500/30 bg-amber-950/30 text-amber-400/80"
      )}>
        <span className={cn("h-1.5 w-1.5 rounded-full", data.embeddingsAvailable ? "bg-green-400" : "bg-amber-400")} />
        <span>Deep Memory: {data.embeddingsAvailable ? "Active" : "Standby"}</span>
      </div>
      {!data.embeddingsAvailable && (
        <div className="text-amber-400/50 px-1">
          Using pattern-based recall
        </div>
      )}
      <div className="text-muted-foreground/50 ml-auto">
        {data.embeddingCalls + data.fallbackCalls} resonance cycles
      </div>
    </div>
  );
}

/**
 * What free/Plus users see in place of the gated panels above: a single
 * compact upsell pill rather than four rows of telemetry they can't act on.
 */
export function ResonanceInsightsTeaser({ onUnlock }: { onUnlock: () => void }) {
  return (
    <button
      type="button"
      onClick={onUnlock}
      className="mb-1.5 flex items-center gap-1.5 rounded-full border border-amber-500/25 bg-amber-500/[0.04] px-2 py-0.5 text-[10px] font-mono text-amber-400/80 transition-colors hover:bg-amber-500/10"
      data-testid="button-resonance-insights-teaser"
    >
      <Sparkles className="h-3 w-3" />
      <span>Resonance Insights — unlock with Pro</span>
    </button>
  );
}
